import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FiSearch, FiX, FiLock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

import { PATHS } from '@/routes/paths';
import { learningObjectivesApi } from '@/services/learningObjectives';
import { extractApiError } from '@/services/errors';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionContent, AccordionTrigger, AccordionTitle, AccordionSubtitle } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableNoData, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';

const DESCRIPTORS_TABLE_HEAD = [{ label: 'Código', width: 130 }, { label: 'Componente', width: 150 }, { label: 'Título' }, { label: 'Peso', width: 80 }, { label: 'Vínculos', width: 140 }, { label: 'Cobertura', width: 120 }];

function percent(ratio) {
  return `${((Number(ratio) || 0) * 100).toFixed(1)}%`;
}

function StatCard({ label, value, accent = 'neutral', hint, small }) {
  const ring = accent === 'primary' ? 'border-primary/20 bg-primary/5' : accent === 'success' ? 'border-success/20 bg-success/5' : accent === 'danger' ? 'border-danger/20 bg-danger/5' : 'border-neutral-lightest bg-white';
  return (
    <div className={`rounded-lg border ${ring} p-3`}>
      <div className="text-xs uppercase tracking-wide text-neutral">{label}</div>
      <div className={`mt-1 font-bold text-foreground ${small ? 'text-base' : 'text-2xl'}`}>{value}</div>
      {hint && <div className="text-xs text-neutral-light">{hint}</div>}
    </div>
  );
}

function CoverageCards({ coverage, objective }) {
  if (!coverage) return null;
  const uncovered = coverage.uncovered_descriptors ?? 0;
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
      <StatCard
        label="Total descritores"
        value={coverage.total_descriptors ?? 0}
        accent="primary"
      />
      <StatCard
        label="Cobertos"
        value={coverage.covered_descriptors ?? 0}
        accent="success"
      />
      <StatCard
        label="Lacunas"
        value={uncovered}
        accent={uncovered > 0 ? 'danger' : 'neutral'}
        hint={uncovered > 0 ? 'bloqueiam a publicação' : 'nenhuma lacuna'}
      />
      <StatCard
        label="Cobertura"
        value={percent(coverage.coverage_ratio)}
        accent={coverage.gate_met ? 'success' : 'neutral'}
        hint={coverage.gate_met ? 'gate 100% atingido' : 'gate exige 100%'}
      />
      <StatCard
        label="Fonte"
        value={objective?.source || '—'}
        accent="neutral"
        small
        hint={objective?.official ? 'oficial' : 'não oficial'}
      />
    </div>
  );
}

function DescriptorsTable({ descritores, onSelect }) {
  return (
    <Table className="min-w-[900px]">
      <TableHeaderWrapper tableHead={DESCRIPTORS_TABLE_HEAD} />
      <TableBody>
        {descritores.map(d => {
          const counts = d.attributes.link_counts || {};
          return (
            <TableRow
              key={d.id}
              onClick={() => onSelect(d)}
              className="hover:bg-primary/5 cursor-pointer"
            >
              <TableCell className="font-mono text-xs font-semibold text-primary-dark">{d.attributes.codigo}</TableCell>
              <TableCell className="text-xs">{d.attributes.componente}</TableCell>
              <TableCell className="text-sm">{d.attributes.titulo}</TableCell>
              <TableCell className="font-mono text-xs">{d.attributes.peso_sugerido ?? '—'}</TableCell>
              <TableCell className="text-xs">
                <span className="text-success">{counts.approved || 0} aprov.</span>
                {(counts.pending || 0) > 0 && <span className="ml-2 text-attention">{counts.pending} pend.</span>}
              </TableCell>
              <TableCell>
                <Badge
                  variant={d.attributes.covered ? 'success-soft' : 'danger-soft'}
                  size="sm"
                >
                  {d.attributes.covered ? 'coberto' : 'lacuna'}
                </Badge>
              </TableCell>
            </TableRow>
          );
        })}
        <TableNoData
          notFound={descritores.length === 0}
          colSpan={DESCRIPTORS_TABLE_HEAD.length}
        />
      </TableBody>
    </Table>
  );
}

function DetailRow({ k, v }) {
  if (v === null || v === undefined || v === '') return null;
  return (
    <div className="flex gap-2 border-b border-neutral-lightest py-1.5 text-sm last:border-0">
      <span className="w-40 shrink-0 text-neutral">{k}</span>
      <span className="flex-1 break-words text-foreground">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
    </div>
  );
}

function DescriptorDetailPanel({ descriptor }) {
  if (!descriptor) return null;
  const a = descriptor.attributes;
  const hints = Array.isArray(a.taxonomy_hints) ? a.taxonomy_hints : [];
  const counts = a.link_counts || {};
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={a.covered ? 'success-soft' : 'danger-soft'}>{a.covered ? 'Coberto' : 'Lacuna — sem vínculo aprovado'}</Badge>
        {a.oficial && <Badge variant="info-soft">Oficial</Badge>}
        {a.etapa_serie && <Badge variant="neutral-lightest">{a.etapa_serie}</Badge>}
      </div>

      <div className="bg-neutral-lightest/30 rounded-lg border border-neutral-lightest p-3">
        <div className="mb-1 text-xs uppercase tracking-wide text-neutral">Descritor (verbatim)</div>
        <p className="whitespace-pre-wrap text-sm text-foreground">{a.descritor}</p>
      </div>

      <div>
        <DetailRow
          k="Área"
          v={a.area}
        />
        <DetailRow
          k="Componente"
          v={a.componente}
        />
        <DetailRow
          k="Peso sugerido"
          v={a.peso_sugerido}
        />
        <DetailRow
          k="Fonte"
          v={a.fonte}
        />
        <DetailRow
          k="Vínculos aprovados"
          v={counts.approved || 0}
        />
        <DetailRow
          k="Vínculos pendentes"
          v={counts.pending || 0}
        />
        <DetailRow
          k="Vínculos rejeitados"
          v={counts.rejected || 0}
        />
      </div>

      {hints.length > 0 && (
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-neutral">Dicas de taxonomia</div>
          <div className="flex flex-wrap gap-1">
            {hints.map(h => (
              <Badge
                key={String(h)}
                variant="neutral-lightest"
                size="sm"
              >
                {String(h)}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PublishModal({ objective, coverage, open, onOpenChange, onPublished }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const gateMet = !!coverage?.gate_met;

  const handlePublish = async () => {
    if (!password.trim()) {
      toast.error('Informe sua senha para continuar.');
      return;
    }
    setSaving(true);
    try {
      await learningObjectivesApi.publish(objective.id, password);
      toast.success(`Objetivo "${objective.attributes?.name || objective.name}" publicado.`);
      setPassword('');
      onOpenChange(false);
      onPublished?.();
    } catch (err) {
      toast.error(extractApiError(err, 'Falha ao publicar objetivo.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) setPassword('');
        onOpenChange(isOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Publicar objetivo</DialogTitle>
          <DialogDescription>A publicação exige 100% dos descritores mapeados (gate de cobertura).</DialogDescription>
        </DialogHeader>

        <div className={`mb-3 flex items-start gap-2 rounded-lg border p-3 text-sm ${gateMet ? 'border-success/30 bg-success/5 text-success' : 'border-danger/30 bg-danger/5 text-danger'}`}>
          {gateMet ? <FiCheckCircle className="mt-0.5 shrink-0" /> : <FiAlertCircle className="mt-0.5 shrink-0" />}
          <span>
            Cobertura atual: {percent(coverage?.coverage_ratio)} ({coverage?.covered_descriptors ?? 0}/{coverage?.total_descriptors ?? 0}).
            {gateMet ? ' Gate atingido — pode publicar.' : ` Faltam ${coverage?.uncovered_descriptors ?? 0} descritores sem vínculo aprovado.`}
          </span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="publish-password">Senha de autorização</Label>
          <Input
            id="publish-password"
            type="password"
            autoComplete="off"
            placeholder="Senha da equipe"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={!gateMet || saving}
          />
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!gateMet || saving}
          >
            <ButtonIcon>
              <FiLock />
            </ButtonIcon>
            {saving ? 'Publicando...' : 'Publicar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReviewObjectives() {
  // A lista de objetivos vem SEMPRE do backend (`index`). Nada de constante
  // hardcoded no frontend: foi exatamente isso que fez a tela da matriz ficar
  // presa em 4 etapas antigas enquanto o banco já tinha 5 objetivos.
  const [objectives, setObjectives] = useState([]);
  const [loadingObjectives, setLoadingObjectives] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState([]);
  const [selectedDescriptor, setSelectedDescriptor] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const breadcrumbs = {
    title: 'Banco de questões',
    nav: [
      { route: PATHS.QUESTIONS.REVIEW_QUEUE, name: 'Revisão pedagógica', isActive: false },
      { name: 'Objetivos de aprendizagem', isActive: true }
    ]
  };

  useEffect(() => {
    setLoadingObjectives(true);
    learningObjectivesApi
      .index()
      .then(json => {
        const list = json?.data || [];
        setObjectives(list);
        setSelectedId(prev => (prev && list.some(o => String(o.id) === String(prev)) ? prev : (list[0]?.id ?? null)));
      })
      .catch(err => {
        toast.error(extractApiError(err, 'Falha ao carregar objetivos de aprendizagem.'));
        setObjectives([]);
      })
      .finally(() => setLoadingObjectives(false));
  }, [reloadToken]);

  useEffect(() => {
    if (!selectedId) {
      setMatrix(null);
      return;
    }
    setLoadingMatrix(true);
    setMatrix(null);
    learningObjectivesApi
      .descriptors(selectedId)
      .then(json => setMatrix(json))
      .catch(err => {
        toast.error(extractApiError(err, 'Falha ao carregar a matriz deste objetivo.'));
        setMatrix(null);
      })
      .finally(() => setLoadingMatrix(false));
  }, [selectedId, reloadToken]);

  // Reset busca e seções ao trocar de objetivo.
  useEffect(() => {
    setSearch('');
    setOpenSections([]);
  }, [selectedId]);

  const selectedObjective = useMemo(() => objectives.find(o => String(o.id) === String(selectedId)) || null, [objectives, selectedId]);

  const coverage = matrix?.meta?.coverage || selectedObjective?.attributes?.coverage || null;
  const objectiveMeta = matrix?.meta?.objective || selectedObjective?.attributes || null;

  // Agrupa por área no cliente — o backend já devolve ordenado por
  // (area, componente, codigo), então a ordem das seções é estável.
  const groups = useMemo(() => {
    const rows = matrix?.data || [];
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? rows
      : rows.filter(d => {
          const a = d.attributes;
          return a.codigo?.toLowerCase().includes(q) || a.titulo?.toLowerCase().includes(q) || a.descritor?.toLowerCase().includes(q) || a.componente?.toLowerCase().includes(q);
        });
    const byArea = new Map();
    filtered.forEach(d => {
      const area = d.attributes.area || 'Sem área';
      if (!byArea.has(area)) byArea.set(area, []);
      byArea.get(area).push(d);
    });
    return Array.from(byArea, ([area, descritores]) => ({
      area,
      descritores,
      uncovered: descritores.filter(d => !d.attributes.covered).length
    }));
  }, [matrix, search]);

  const totalShown = groups.reduce((acc, g) => acc + g.descritores.length, 0);
  const totalRows = matrix?.data?.length || 0;
  const published = !!objectiveMeta?.published_at || !!objectiveMeta?.published;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">Objetivos de aprendizagem</h2>
          <p className="text-sm text-neutral">Matriz de referência por objetivo, cobertura de descritores e publicação com gate de 100%.</p>
        </div>
        {selectedObjective && (
          <div className="flex items-center gap-2">
            {published ? <Badge variant="success-soft">Publicado</Badge> : <Badge variant="muted">Rascunho</Badge>}
            <Button
              onClick={() => setPublishOpen(true)}
              disabled={published}
            >
              <ButtonIcon>
                <FiLock />
              </ButtonIcon>
              {published ? 'Já publicado' : 'Publicar'}
            </Button>
          </div>
        )}
      </div>

      {loadingObjectives ? (
        <Loader />
      ) : objectives.length === 0 ? (
        <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Nenhum objetivo de aprendizagem cadastrado.</div>
      ) : (
        <>
          <div className="mb-4">
            <Tabs
              value={String(selectedId ?? '')}
              onValueChange={value => setSelectedId(value)}
            >
              <TabsList>
                {objectives.map(o => (
                  <TabsTrigger
                    key={o.id}
                    value={String(o.id)}
                  >
                    {o.attributes.name}
                    <span className="ml-2 text-xs text-neutral">· {o.attributes.coverage?.total_descriptors ?? 0}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {loadingMatrix ? (
            <Loader />
          ) : !matrix ? (
            <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Não foi possível carregar a matriz deste objetivo.</div>
          ) : (
            <>
              <CoverageCards
                coverage={coverage}
                objective={objectiveMeta}
              />

              <div className="mb-4 flex items-center gap-3 rounded-lg border border-neutral-lightest bg-white p-3">
                <div className="relative max-w-md flex-1">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral" />
                  <Input
                    type="search"
                    placeholder="Buscar por código, título, descritor, componente..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <span className="text-xs text-neutral">
                  {totalShown} de {totalRows} descritores
                </span>
                {search && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setSearch('')}
                  >
                    <ButtonIcon>
                      <FiX />
                    </ButtonIcon>
                    Limpar busca
                  </Button>
                )}
              </div>

              {groups.length === 0 ? (
                <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Nenhum descritor encontrado.</div>
              ) : (
                <Accordion
                  type="multiple"
                  value={search.trim() ? groups.map(g => g.area) : openSections}
                  onValueChange={setOpenSections}
                >
                  {groups.map(g => (
                    <AccordionItem
                      key={g.area}
                      value={g.area}
                    >
                      <AccordionTrigger>
                        <AccordionTitle>{g.area}</AccordionTitle>
                        <AccordionSubtitle>
                          {g.descritores.length} descritores
                          {g.uncovered > 0 && <span className="ml-2 text-danger">· {g.uncovered} lacunas</span>}
                        </AccordionSubtitle>
                      </AccordionTrigger>
                      <AccordionContent>
                        <DescriptorsTable
                          descritores={g.descritores}
                          onSelect={setSelectedDescriptor}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </>
          )}
        </>
      )}

      <Dialog
        open={!!selectedDescriptor}
        onOpenChange={isOpen => {
          if (!isOpen) setSelectedDescriptor(null);
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="font-mono">{selectedDescriptor?.attributes?.codigo}</DialogTitle>
            <DialogDescription>{selectedDescriptor?.attributes?.titulo}</DialogDescription>
          </DialogHeader>
          <DescriptorDetailPanel descriptor={selectedDescriptor} />
        </DialogContent>
      </Dialog>

      {selectedObjective && (
        <PublishModal
          objective={selectedObjective}
          coverage={coverage}
          open={publishOpen}
          onOpenChange={setPublishOpen}
          onPublished={() => setReloadToken(t => t + 1)}
        />
      )}
    </DashboardContent>
  );
}
